import React, { useEffect } from "react";
import { useParams } from "react-router";
import { useAppSelector } from "../../../store/Hooks";
import { AnswerFileResult } from "../../../store/viewmodels/AnswerFileViewModel";
import { AnswerSetResult } from "../../../store/viewmodels/AnswerSetViewModel";
import { AnswerResult } from "../../../store/viewmodels/AnswerViewModel";
import { Itemset, ItemsetResult } from "../../../store/viewmodels/ItemsetViewModel";
import { StudentData } from "../../../store/viewmodels/StudentViewModel";
import { Task } from "../../../store/viewmodels/TaskViewModel";
import { getBadges, getTrophys } from "../../utils/Accessories";
import { getDurationInSeconds, getMins, getSeconds } from "../../utils/DateServices";
import ModalWrapper from "../../utils/modal/ModalWrapper";
import StudentBasicResult from "./StudentBasicResult";
import StudentMemoryGameResult from "./StudentMemoryGameResult";
import StudentVideoModal from "./StudentVideoModal";
import StudentVideoRecordingResult from "./StudentVideoRecordingResult";


interface StudentResultInfoParams {
    taskId?: string,
}

function StudentResultInfo(): JSX.Element {
    const studentData: StudentData = useAppSelector(state => state.studentData);
    const tasks_access_list: Task[] = studentData.student.task_access_list;
    const itemset_access_list: ItemsetResult[] = studentData.student.active_item_sets;
    const answerSets: AnswerSetResult[] | undefined = studentData.student.results;

    let { taskId } = useParams<StudentResultInfoParams>();

    const [isModalVisible, setModalVisible] = React.useState(false);
    const [content, setContent] = React.useState(<></>);
    const showModal = () => {
        setModalVisible(wasModalVisible => !wasModalVisible);
    }

    const [answerFileToShow, setAnswerFileToShow] = React.useState(undefined as AnswerFileResult | undefined);

    useEffect(() => {
        let videoContent = <StudentVideoModal answer={answerFileToShow} close={showModal} />;
        setContent(videoContent);
    }, [answerFileToShow])

    const task: Task | undefined = tasks_access_list.find(t => t.id.toString() === taskId);
    let element = <div></div>;
    let header = "";
    if (task !== undefined && answerSets !== undefined) {
        header = task.name;
        let taskItemsets: Itemset[] = task.itemsets;
        let tasksAnswerSets: AnswerSetResult[] = [] as AnswerSetResult[];

        for (let i: number = 0; i < taskItemsets.length; i++) {
            for (let as: number = 0; as < answerSets.length; as++) {
                if (taskItemsets[i]?.id === answerSets[as]?.itemset?.id) {
                    tasksAnswerSets.push(answerSets[as]);
                }
            }
        }

        let itemsetItems = taskItemsets.map(itemset => <ItemsetResultList key={"itemsetResultList" + itemset.id} itemset={itemset} itemset_access_list={itemset_access_list} answerSets={tasksAnswerSets} task={task} showModal={showModal} setAnswerToWatch={setAnswerFileToShow} />);
        element = <div>{itemsetItems}</div>;
    }

    return (
        <div className="w-full">
            {<ModalWrapper isVisible={isModalVisible} onBackdropClick={showModal} content={content} type={"result"} />}
            <h1 className="text-3xl font-bold text-teal-700 m-4">{header}</h1>
            {element}
        </div>
    );
}

interface ItemsetResultListProps {
    task: Task,
    itemset: Itemset,
    itemset_access_list: ItemsetResult[],
    answerSets: AnswerSetResult[],
    showModal: () => void,
    setAnswerToWatch: React.Dispatch<React.SetStateAction<AnswerFileResult | undefined>>,
}

function ItemsetResultList(props: ItemsetResultListProps): JSX.Element {
    const itemsetId = props.itemset.id;
    const active: boolean = props.itemset_access_list.find(t => t.id === itemsetId) !== undefined ? true : false;

    if (!active) return <></>;

    const itemsetAnswers = [];

    for (let i: number = 0; i < props.answerSets.length; i++) {
        let answerSet = props.answerSets[i];
        if (answerSet.itemset.id === props.itemset.id) {
            itemsetAnswers.push(answerSet);
        }
    }

    let allSeconds: number = 0;

    for (let i: number = 0; i < itemsetAnswers.length; i++) {
        let startTime = new Date(Date.parse(itemsetAnswers[i].start_time));
        let endTime = new Date(Date.parse(itemsetAnswers[i].end_time));

        let secs = getDurationInSeconds(endTime, startTime);

        if (!isNaN(secs)) {
            allSeconds = allSeconds + secs;
        }
    }

    let answerCount = itemsetAnswers.length;
    let min = getMins(allSeconds);
    let s = getSeconds(allSeconds);

    const time = min + " min ja " + s + " s."

    let trophys = getTrophys(min, props.itemset.id);
    let badges = getBadges(answerCount, props.itemset.id);

    let videoRecordingAnswers = [];
    let memoryGameAnswers = [];
    let basicResults = [];

    for (let i: number = 0; i < itemsetAnswers.length; i++) {
        let itemsetAnswer = itemsetAnswers[i];

        let findErrorAnswers = [];
        let organizeAnswers = [];
        let oneChoiceAnswers = [];
        let combineAnswers = [];

        for (let j: number = 0; j < itemsetAnswer.answers.length; j++) {
            let answer = itemsetAnswer.answers[j];
            let itemtype = getType(answer, itemsetAnswer, props.task)

            switch (itemtype) {
                case "video-recording": {
                    videoRecordingAnswers.push(answer);
                    break;
                }
                case "memory-game": {
                    memoryGameAnswers.push(answer);
                    break;
                }
                case "find-error": {
                    findErrorAnswers.push(answer);
                    break;
                }
                case "organize": {
                    organizeAnswers.push(answer);
                    break;
                }
                case "one-choice": {
                    oneChoiceAnswers.push(answer);
                    break;
                }
                case "combine": {
                    combineAnswers.push(answer);
                    break;
                }
                case "video-instruction": {
                    break;
                }
                case "NONE": {
                    break;
                }
                default: {
                    break;
                }
            }
        }

        if (findErrorAnswers.length > 0 || combineAnswers.length > 0 || organizeAnswers.length > 0 || oneChoiceAnswers.length > 0) {
            basicResults.push(<StudentBasicResult key={"Basic_" + itemsetAnswer.id} answerSet={itemsetAnswer} findErrorAnswers={findErrorAnswers} combineAnswers={combineAnswers} organizeAnswers={organizeAnswers} oneChoiceAnswers={oneChoiceAnswers} />);
        }
    }

    let memoryGameResults = memoryGameAnswers.length > 0 ? <StudentMemoryGameResult key={"memoryGame_" + itemsetId} answers={memoryGameAnswers} /> : <></>;
    let videoRecordingResults = videoRecordingAnswers.length > 0 ? <StudentVideoRecordingResult key={"VideoRecording_" + itemsetId} answers={videoRecordingAnswers} itemset={props.itemset} showModal={props.showModal} setAnswerToWatch={props.setAnswerToWatch} /> : <></>;

    let basicElement = basicResults.length <= 0 ? <></> :
        <div key={"Basic_" + itemsetId} className="">
            <h1 className="m-auto text-center text-2xl text-teal-500 font-bold">Tulokset</h1>
            <div className="border border-teal-200 my-2 p-2">
                <div className="flex">
                    <div className="w-1/12 ml-0 mr-1">Oikeita</div>
                    <div className="w-1/12 mr-1 ml-1">Max</div>
                    <div className="w-1/3 mr-1 ml-1">PVM</div>
                    <div className="mr-0 ml-1"></div>
                </div>
                {basicResults}
            </div>
        </div>;

    return (
        <div className={"px-4 w-full h-full block"} key={"itemsetResultList" + props.itemset.name + props.itemset.id}>
            <div>
                <span className={"text-3xl font-bold "}>{props.itemset.name}</span>
            </div>
            <div className="flex">
                <div className="w-1/2 min-h-32 border border-teal-200 my-2 mr-1 p-2">
                    <span>{"Olet tehnyt tehtävän "}</span>
                    <span className="font-bold">{answerCount}</span>
                    <span>{" kertaa"}</span>
                    {badges}
                </div>
                <div className="w-1/2 min-h-32 border border-teal-200 my-2 ml-1 p-2">
                    <span>{"Olet harjoitellut "}</span>
                    <span className="font-bold">{time}</span>
                    {trophys}
                </div>
            </div>
            {memoryGameResults}
            {videoRecordingResults}
            {basicElement}
        </div>
    );
}

function getType(answer: AnswerResult, answerSet: AnswerSetResult, task: Task): string {
    if (answer?.item?.type && answer.item.type !== "NONE") return answer.item.type;
    if (answerSet?.itemset?.type && answerSet.itemset.type !== "NONE") return answerSet.itemset.type;
    if (task?.type) return task.type;

    return 'NONE';
}

export default StudentResultInfo;